import { makeStyles } from "tss-react/mui"
import { Grid, MenuItem, Stack, Typography } from "@mui/material"
import { DateTime } from "luxon"

import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { fetchShopsList, ShopsListResponse, ShopsQuery } from "../../shops/shopsApi"
import {
    FormikDropdown,
    FormikSwitch,
    FormikTextArea,
    FormikTextField,
    FormikTimePicker,
} from "../../../../../shared/components/forms/Formik"
import { StandardForm } from "../../../../../shared/components/forms/StandardForm"
import { StandardMainPanel } from "../../../../../shared/components/forms/StandardMainPanel"
import { StandardSidePanel } from "../../../../../shared/components/forms/StandardSidePanel"

import { CreateOrEditDeliveryLocationFormValues } from "../deliveryLocationsTypes"

const selectShops = (data: ShopsListResponse) => data?.shops || []

const useStyles = makeStyles()((theme) => ({
    sectionTitle: {
        fontWeight: 600,
        color: theme.palette.tailwind.slate[700],
    },
    helperText: {
        color: theme.palette.tailwind.slate[500],
    },
}))

export const FormDeliveryLocationContent = () => {
    const { classes } = useStyles()
    const { merchantId } = useMerchant()

    const { data: shopsList = [] } = useApiQuery(fetchShopsList, {
        queryName: ShopsQuery.LIST,
        dependencies: {
            merchantId,
        },
        select: selectShops,
    })

    return (
        <StandardForm>
            <StandardMainPanel>
                <LocalizedStrict id="merchant-delivery-location-form-field-name" attrs={{ label: true }}>
                    <FormikTextField<CreateOrEditDeliveryLocationFormValues> name="name" label="Name" fullWidth />
                </LocalizedStrict>
                <LocalizedStrict id="merchant-delivery-location-form-field-webshop" attrs={{ label: true }}>
                    <FormikDropdown<CreateOrEditDeliveryLocationFormValues> name="webshopId" label="Webshop" fullWidth>
                        {shopsList.map((shop) => (
                            <MenuItem key={shop.id} value={String(shop.id)}>
                                {shop.name}
                            </MenuItem>
                        ))}
                    </FormikDropdown>
                </LocalizedStrict>
                <Stack gap={1}>
                    <LocalizedStrict id="merchant-delivery-location-form-pickup-details-title">
                        <Typography variant="subtitle1" className={classes.sectionTitle}>
                            Pickup details:
                        </Typography>
                    </LocalizedStrict>
                    <LocalizedStrict id="merchant-delivery-location-form-field-description" attrs={{ label: true }}>
                        <FormikTextArea<CreateOrEditDeliveryLocationFormValues>
                            name="description"
                            label="Description"
                            fullWidth
                            rows={3}
                        />
                    </LocalizedStrict>
                </Stack>
                <LocalizedStrict id="merchant-delivery-location-form-pickup-time-helper">
                    <Typography variant="body2" className={classes.helperText}>
                        Select the pick up time, so customer knows the time period to pickup the order:
                    </Typography>
                </LocalizedStrict>
                <Grid container item gap={2} sm={12} direction="row">
                    <Grid item sm={3}>
                        <LocalizedStrict id="merchant-delivery-location-form-start-time-picker" attrs={{ label: true }}>
                            <FormikTimePicker<CreateOrEditDeliveryLocationFormValues, DateTime>
                                label="From:"
                                name="timeIntervalStart"
                            />
                        </LocalizedStrict>
                    </Grid>
                    <Grid item sm={3}>
                        <LocalizedStrict id="merchant-delivery-location-form-end-time-picker" attrs={{ label: true }}>
                            <FormikTimePicker<CreateOrEditDeliveryLocationFormValues, DateTime>
                                label="To:"
                                name="timeIntervalEnd"
                            />
                        </LocalizedStrict>
                    </Grid>
                </Grid>
            </StandardMainPanel>
            <LocalizedStrict id="merchant-delivery-location-form-settings" attrs={{ title: true }}>
                <StandardSidePanel variant="collapsible" title="Settings">
                    <LocalizedStrict id="merchant-delivery-location-form-field-active">
                        <FormikSwitch<CreateOrEditDeliveryLocationFormValues> name="isActive">Active:</FormikSwitch>
                    </LocalizedStrict>
                </StandardSidePanel>
            </LocalizedStrict>
        </StandardForm>
    )
}

